import { useEffect, useState } from 'react';
import Spinner from 'react-spinkit';
import { ReportingMixpanelEvent } from '@peekapak/types/ReportDataExchangeTypes';
import { getLegacyStoryOpenEvents, getNewStoryOpenEvents } from '../reportUtilities';
import Table from '../Tables/Table';
import styles from './MostReadStories.module.scss';
import generalStyles from './SchoolReport.module.scss';
import associationData from '../../ClassroomDashboard/Associations.json';

interface Props {
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  newMixpanelEvents: ReportingMixpanelEvent[];
  eventsReady: boolean;
}

interface AssociationEntry {
  unit: number;
  displayName: string;
  description?: string;
}

const associations = (associationData as { associations: Record<string, AssociationEntry> }).associations;

const bookNumberToDisplayName: Record<string, string> = {};
Object.entries(associations).forEach(([key, entry]) => {
  if (key.startsWith('book_')) {
    bookNumberToDisplayName[key] = entry.displayName;
  }
});

function getEventStoryName(event: ReportingMixpanelEvent): string {
  const evt = event as any;

  const book = evt.book;
  if (book !== null && book !== undefined && bookNumberToDisplayName[`book_${book}`]) {
    return bookNumberToDisplayName[`book_${book}`];
  }

  const unitName = evt.unit as string | undefined;
  if (unitName) return unitName;

  const story = evt.story as string | undefined;
  if (story) return story;

  const quest = evt.quest as string | undefined;
  if (quest) return quest;

  const legacyMatch = event.name.match(/^story(\d+)pg1/);
  if (legacyMatch && bookNumberToDisplayName[`book_${legacyMatch[1]}`]) {
    return bookNumberToDisplayName[`book_${legacyMatch[1]}`];
  }

  const featuredMatch = event.name.match(/^featuredstory[_ ](.+)/i);
  if (featuredMatch) {
    return featuredMatch[1].replace(/[_-]/g, ' ');
  }

  return event.name;
}

const MostReadStories = ({
  legacyPlatformEvents,
  legacyPeekavilleEvents,
  newMixpanelEvents,
  eventsReady,
}: Props): JSX.Element => {
  const [data, setData] = useState<{ name: string; count: number }[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (eventsReady) {
      buildData();
    }
  }, [eventsReady, legacyPlatformEvents, legacyPeekavilleEvents, newMixpanelEvents]);

  function buildData() {
    const allEvents = [...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents];

    const storyEvents = [...getLegacyStoryOpenEvents(allEvents), ...getNewStoryOpenEvents(allEvents)];

    const storyCounts: Record<string, number> = {};

    storyEvents.forEach((event) => {
      const name = getEventStoryName(event);
      storyCounts[name] = (storyCounts[name] || 0) + 1;
    });

    const result = Object.entries(storyCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    setData(result);
    setReady(true);
  }

  return (
    <div className={styles.mostReadStories}>
      <h4 className={generalStyles.tableHeader}>Most Read Stories</h4>
      <Table tableHeader={[{ title: 'Story' }, { title: 'Reads' }]}>{getContent()}</Table>
    </div>
  );

  function getContent() {
    if (!ready) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>
              Loading
              <Spinner name='line-scale' />
            </div>
          </td>
        </tr>
      );
    }

    if (data.length === 0) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>No data available</div>
          </td>
        </tr>
      );
    }

    return (
      <>
        {data.map((d) => (
          <tr key={d.name}>
            <td>{d.name}</td>
            <td>{d.count}</td>
          </tr>
        ))}
      </>
    );
  }
};

export default MostReadStories;
